import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight, Calendar, Check, CreditCard, Hammer, Moon, PlayCircle,
  Sparkles, StickyNote, Sun, Timer, TrendingUp, Users, X,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import Wordmark from '../brand/Wordmark';
import { API_URL } from '../../constants/api';
import './LandingPage.css';

// Fallbacks mirror backend/models/billing.py PLAN_CATALOG. The live catalog is
// fetched below and wins; these only render if the API is unreachable, so the
// page never shows an empty price.
const FALLBACK_PRICING = {
  free: { ai_messages_per_day: 5 },
  solo: { price_cents: 2500 },
  team: { price_cents: 2000, seat_limit: 7, seat_min: 2 },
};

// Currency symbol comes from the plan catalog too, so there is exactly one
// place to change it. The literal is only the offline fallback.
const FALLBACK_SYMBOL = '€';

const money = (cents, symbol = FALLBACK_SYMBOL) => `${symbol}${Math.round(cents / 100)}`;

/**
 * LandingPage — the public front door at `/` for logged-out visitors.
 *
 * Before this existed, foundersplus.nl rendered LoginForm immediately, so
 * nobody could see the product without creating an account and search
 * engines had nothing to index.
 *
 * Styling is scoped under `.fplp` (see LandingPage.css) so its generic class
 * names and design tokens can't leak into the authenticated app.
 *
 * Signed-in visitors see this page too — "/" is the homepage for everyone.
 * For them the in-page calls to action become "Open dashboard" and skip the
 * auth forms entirely; they should never be asked to sign in or sign up twice.
 * The nav button is the exception: it always reads "Sign in" and only its
 * destination changes (see navLabel below).
 *
 * Props:
 *   isAuthenticated — whether the visitor already has a live session
 *   onSignIn()      — open the login form
 *   onSignUp()      — open the register form
 *   onEnterApp()    — go straight to the dashboard (signed-in visitors)
 *   onShowLegal(id) — 'terms' | 'privacy' | 'disclaimer'
 */

// The eight tools a solo founder juggles, and roughly where each sits before
// they converge. x/y/rot feed CSS custom properties on .chip.
const SCATTER = [
  { label: 'Issue tracker', x: -330, y: -84, r: -7 },
  { label: 'Spreadsheet', x: -142, y: -116, r: 4 },
  { label: 'Notes app', x: 70, y: -108, r: -4 },
  { label: 'Chat AI', x: 268, y: -76, r: 8 },
  { label: 'Calendar', x: -300, y: 74, r: 6 },
  { label: 'Timer', x: -104, y: 112, r: -5 },
  { label: 'Roadmap doc', x: 108, y: 118, r: 3 },
  { label: 'Runway sheet', x: 302, y: 80, r: -8 },
];

const TOOLS = [
  {
    key: 'build', Icon: Hammer, name: 'Build',
    copy: 'Issues, priorities and AI task breakdown. A priority matrix for when everything feels urgent.',
  },
  {
    key: 'money', Icon: CreditCard, name: 'Money',
    copy: 'Cash, burn and runway in plain numbers. 30+ currencies, so it works wherever you are building.',
  },
  {
    key: 'road', Icon: Calendar, name: 'Roadmap',
    copy: 'Milestones and deadlines on a calendar you will actually look at — and that the AI reads.',
  },
  {
    key: 'focus', Icon: Timer, name: 'Focus',
    copy: 'Deep-work sessions and time blocks. Momentum and streaks that count real shipped work, not logins.',
  },
  {
    key: 'notes', Icon: StickyNote, name: 'Notes',
    copy: 'Catch the idea in two seconds. Promote it to an issue or a milestone when it turns out to matter.',
  },
  {
    key: 'team', Icon: Users, name: 'Team',
    copy: 'For the day you stop being solo. Workload, objectives and group chat for up to four people.',
  },
];

const AI_POINTS = [
  ['A daily brief', 'that names the one thing worth doing before noon.'],
  ['Ask AI on any issue', '— it pulls the full ticket in as context, no copy-paste.'],
  ['Task breakdown', 'that turns "launch the beta" into a list you can actually start.'],
  ['Priority triage', 'that sorts your queue against the deadline and the money.'],
];

const FAQ = [
  ['Do I need to install anything?',
    'No. Founders+ runs in the browser on desktop and phone. On your phone you can add it to your home screen and it opens like an app — but there is no App Store download and no native app to keep updated.'],
  ['How is this different from Notion or Linear?',
    'Those are excellent, general tools you have to assemble into a system yourself. Founders+ arrives already assembled around one job — running a company alone — and the AI can read across every part of it without you wiring anything up.'],
  ['Do I have to give it my real financials?',
    'Only if you want the runway number. Money is a manual entry tool — there is no bank connection and nothing is pulled from your accounts. Skip it and everything else still works.'],
  ['What happens when my free AI messages run out?',
    'Everything else keeps working — your issues, notes, roadmap and finance stay fully open on the free plan forever. Only the AI pauses until tomorrow.'],
  ['How do I sign in?',
    'Google, or an email and password if you would rather not use Google.'],
  ['Can I cancel?',
    'Any time, from your billing settings — it is self-serve, no email to support. You keep access until the end of the period you have paid for.'],
];

const LandingPage = ({
  isAuthenticated, onSignIn, onSignUp, onEnterApp, onShowLegal,
}) => {
  const { isDark, toggleTheme } = useTheme();

  // The nav button is the registered user's way back in; the in-page buttons
  // are the acquisition path.
  //
  // The nav button always reads "Sign in" — it used to flip to "Open dashboard"
  // whenever a token was present, but that token is only optimistically trusted
  // at boot (App.js validates it asynchronously), so a stale one made the
  // homepage advertise a session the visitor did not have. The label is now
  // constant; only the destination adapts, so a visitor with a live session is
  // still taken straight in rather than shown a pointless login form.
  const navLabel = 'Sign in';
  const navAction = isAuthenticated ? onEnterApp : onSignIn;
  const ctaLabel = isAuthenticated ? 'Open dashboard' : 'Start free';
  const ctaAction = isAuthenticated ? onEnterApp : onSignUp;

  // Pricing comes from the backend plan catalog so this page can never
  // advertise a price checkout won't honour. GET /billing/plans is public.
  const [pricing, setPricing] = useState(FALLBACK_PRICING);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/billing/plans`)
      .then((r) => (r.ok ? r.json() : null))
      .then((list) => {
        if (cancelled || !Array.isArray(list) || !list.length) return;
        setPricing(Object.fromEntries(list.map((p) => [p.id, p])));
      })
      .catch(() => {/* keep the fallback copy */});
    return () => { cancelled = true; };
  }, []);

  const freeAiPerDay = pricing.free?.ai_messages_per_day ?? 5;
  const soloCents = pricing.solo?.price_cents ?? 2500;
  const teamSeatCents = pricing.team?.price_cents ?? 2000;
  const teamSeatLimit = pricing.team?.seat_limit ?? 7;
  const teamSeatMin = pricing.team?.seat_min ?? 2;
  const symbol = pricing.solo?.currency_symbol ?? FALLBACK_SYMBOL;
  const [stuck, setStuck] = useState(false);
  const convergeRef = useRef(null);
  const rootRef = useRef(null);

  // Hairline under the sticky nav once the page has moved.
  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Reveal-on-scroll, plus the one orchestrated moment: the eight scattered
  // tool chips collapsing into a single card.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const rises = Array.from(root.querySelectorAll('.rise'));

    if (!('IntersectionObserver' in window)) {
      rises.forEach((el) => el.classList.add('is-in'));
      convergeRef.current?.classList.add('is-merged');
      return undefined;
    }

    const reveal = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          reveal.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.1 });
    rises.forEach((el) => reveal.observe(el));

    const merge = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-merged');
          merge.disconnect();
        }
      });
    }, { threshold: 0.55 });
    if (convergeRef.current) merge.observe(convergeRef.current);

    return () => { reveal.disconnect(); merge.disconnect(); };
  }, []);

  const jump = (id) => (e) => {
    e.preventDefault();
    rootRef.current
      ?.querySelector(`#${id}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="fplp" ref={rootRef}>
      {/* ── nav ────────────────────────────────────────────────────────── */}
      <header className={`nav${stuck ? ' is-stuck' : ''}`}>
        <div className="shell nav-in">
          <a className="brand" href="#top" onClick={jump('top')}>
            <Wordmark plusColor="var(--brand-hi)" style={{ height: '0.74em', width: 'auto' }} />
          </a>
          <nav className="nav-links">
            <a href="#how" onClick={jump('how')}>How it works</a>
            <a href="#ai" onClick={jump('ai')}>AI co-founder</a>
            <a href="#tools" onClick={jump('tools')}>Tools</a>
            <a href="#pricing" onClick={jump('pricing')}>Pricing</a>
          </nav>
          <div className="nav-cta">
            <button type="button" className="btn btn-primary" onClick={navAction}>
              {navLabel}
            </button>
            <button
              type="button"
              className="theme-btn"
              onClick={toggleTheme}
              aria-label="Toggle colour theme"
            >
              {isDark ? <Sun /> : <Moon />}
            </button>
          </div>
        </div>
      </header>

      {/* ── hero ───────────────────────────────────────────────────────── */}
      <section className="hero" id="top">
        <div className="shell hero-grid">
          <div>
            <span className="tag">
              <span className="tag-dot" /> In private beta — early access open
            </span>
            <h1>Build like you have a <em>co-founder</em>.</h1>
            <p className="hero-sub">
              Founders+ is one calm dashboard for the whole company you are running
              alone — your build queue, your runway, your roadmap, and an AI
              co-founder that actually knows what is in all three.
            </p>
            <div className="hero-cta">
              <button type="button" className="btn btn-primary btn-lg" onClick={ctaAction}>
                {ctaLabel}
                <ArrowRight />
              </button>
              <a className="btn btn-ghost btn-lg" href="#how" onClick={jump('how')}>
                <PlayCircle />
                See it work
              </a>
            </div>
            <div className="hero-note">
              <span><Check /> No card to start</span>
              <span><Check /> Works in any browser</span>
              <span><Check /> Set up in 3 minutes</span>
            </div>
          </div>

          <div className="split-visual">
            <div className="frame">
              <div className="frame-bar">
                <span className="dot" /><span className="dot" /><span className="dot" />
                <span className="frame-url">foundersplus.nl</span>
              </div>
              <div className="frame-body">
                <div className="app-head">
                  <div>
                    <div className="app-hi">Good morning, founder</div>
                    <div className="app-date">Friday, 31 July · Day 12 streak</div>
                  </div>
                  <span className="app-badge">
                    <TrendingUp size={12} />
                    <span className="num">148</span> days runway
                  </span>
                </div>

                <div className="app-card">
                  <div className="app-card-h">
                    <span>Top 3 today</span>
                    <span className="num">1/3</span>
                  </div>
                  <div className="task is-done">
                    <span className="task-box"><Check /></span>
                    <span className="task-t">Fix Google sign-in redirect on iOS Safari</span>
                    <span className="pill pill-low">done</span>
                  </div>
                  <div className="task">
                    <span className="task-box" />
                    <span className="task-t">Ship public landing page</span>
                    <span className="pill pill-hi">high</span>
                  </div>
                  <div className="task">
                    <span className="task-box" />
                    <span className="task-t">Turn on Stripe checkout</span>
                    <span className="pill pill-med">med</span>
                  </div>
                </div>

                <div className="tiles">
                  <div className="tile">
                    <div className="tile-ico ico-build"><Hammer /></div>
                    <div className="tile-n">Build</div>
                    <div className="tile-m"><span className="num">7</span> open</div>
                  </div>
                  <div className="tile">
                    <div className="tile-ico ico-money"><CreditCard /></div>
                    <div className="tile-n">Money</div>
                    <div className="tile-m"><span className="num">$8.4k</span> cash</div>
                  </div>
                  <div className="tile">
                    <div className="tile-ico ico-road"><Calendar /></div>
                    <div className="tile-n">Roadmap</div>
                    <div className="tile-m">Beta in <span className="num">9</span>d</div>
                  </div>
                  <div className="tile">
                    <div className="tile-ico ico-focus"><Timer /></div>
                    <div className="tile-n">Focus</div>
                    <div className="tile-m"><span className="num">2.5</span>h today</div>
                  </div>
                  <div className="tile">
                    <div className="tile-ico ico-notes"><StickyNote /></div>
                    <div className="tile-n">Notes</div>
                    <div className="tile-m"><span className="num">3</span> new</div>
                  </div>
                  <div className="tile">
                    <div className="tile-ico ico-team"><Users /></div>
                    <div className="tile-n">Team</div>
                    <div className="tile-m">Solo</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 8 → 1 ──────────────────────────────────────────────────────── */}
      <section className="band band-sunk" id="how">
        <div className="shell">
          <div className="band-head band-head-c rise">
            <p className="eyebrow">The problem</p>
            <h2>You are not disorganised. You are just paying rent on eight tabs.</h2>
            <p>
              Issues in one tool, money in a spreadsheet, roadmap in a doc, and an AI
              that has never seen any of it. Founders+ puts the whole thing in one
              place — so the context stops living in your head.
            </p>
          </div>

          <div className="converge" ref={convergeRef}>
            <div className="scatter">
              {SCATTER.map(({ label, x, y, r }) => (
                <span
                  key={label}
                  className="chip"
                  style={{ '--x': `${x}px`, '--y': `${y}px`, '--r': `${r}deg` }}
                >
                  <span className="chip-dot" />{label}
                </span>
              ))}
            </div>
            <div className="merged">
              <span>
                <span className="merged-t">One dashboard</span>
                <span className="merged-s">Everything above, talking to each other.</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI ─────────────────────────────────────────────────────────── */}
      <section className="band" id="ai">
        <div className="shell split split-flip">
          <div className="split-visual rise">
            <div className="frame">
              <div className="frame-bar">
                <span className="dot" /><span className="dot" /><span className="dot" />
                <span className="frame-url">AI co-founder</span>
              </div>
              <div className="chat">
                <div className="msg-you">I have got 4 hours today. Billing or the onboarding fix?</div>
                <div className="msg-ai">
                  <div className="msg-ai-h"><Sparkles /> Founders+ AI</div>
                  <p><b>Billing.</b> Three reasons, and they are all in your own data:</p>
                  <ol>
                    <li>
                      You are <b>148 days</b> from zero and have never charged anyone.
                      Onboarding polish does not change that number; billing does.
                    </li>
                    <li>
                      Onboarding is blocked anyway — no traffic reaches it until the
                      landing page ships.
                    </li>
                    <li>
                      Stripe is already written. It is a config task, not a build task.
                      That fits 4 hours; onboarding does not.
                    </li>
                  </ol>
                  <div className="ctx">
                    <span className="ctx-chip">read: runway 148d</span>
                    <span className="ctx-chip">read: 7 open issues</span>
                    <span className="ctx-chip">read: beta milestone, 9d</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rise">
            <p className="eyebrow">The difference</p>
            <h2>An AI that has read your runway, not just your question.</h2>
            <p className="lede">
              Generic chatbots give generic advice because they start every
              conversation blank. Yours starts with your open issues, your cash
              position, and your next deadline already in hand.
            </p>
            <div className="ticks">
              {AI_POINTS.map(([lead, rest]) => (
                <div className="tick" key={lead}>
                  <span className="tick-ico"><Check /></span>
                  <span><b>{lead}</b> {rest}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── tools ──────────────────────────────────────────────────────── */}
      <section className="band band-sunk" id="tools">
        <div className="shell">
          <div className="band-head rise">
            <p className="eyebrow">What is inside</p>
            <h2>Six tools. One login. No integrations to babysit.</h2>
            <p>Each one is a real part of the app, not a roadmap promise.</p>
          </div>
          <div className="tools rise">
            {TOOLS.map(({ key, Icon, name, copy }) => (
              <div className="tool" key={key}>
                <div className={`tool-ico ico-${key}`}><Icon /></div>
                <h3>{name}</h3>
                <p>{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── pricing ────────────────────────────────────────────────────── */}
      <section className="band" id="pricing">
        <div className="shell">
          <div className="band-head band-head-c rise">
            <p className="eyebrow">Pricing</p>
            <h2>Free forever for the tracking. Pay when the AI earns it.</h2>
            <p>Cancel in two clicks from your billing settings. No annual lock-in.</p>
          </div>

          <div className="prices rise">
            <div className="price">
              <div>
                <div className="price-n">Free</div>
                <div className="price-amt"><b>{money(0, symbol)}</b><span className="price-per">/ month</span></div>
                <p className="price-for">For getting your work out of your head.</p>
              </div>
              <ul>
                <li><Check />Build, Money, Roadmap, Focus, Notes — all unlimited</li>
                <li><Check /><span className="num">{freeAiPerDay}</span>&nbsp;AI co-founder messages a day</li>
                <li><Check />Daily brief and momentum tracking</li>
                <li className="off"><X />Team Hub</li>
              </ul>
              <button type="button" className="btn btn-ghost" onClick={ctaAction}>
                {isAuthenticated ? 'Open dashboard' : 'Create an account'}
              </button>
            </div>

            <div className="price price-feature">
              <div>
                <div className="price-n">Solo</div>
                <div className="price-amt">
                  <b>{money(soloCents, symbol)}</b>
                  <span className="price-per">/ month</span>
                </div>
                <p className="price-for">For the founder who talks to the AI every morning.</p>
              </div>
              <ul>
                <li><Check />Everything in Free</li>
                <li><Check />AI co-founder unlocked — no daily cap</li>
                <li><Check />Full AI insights: triage, breakdown, runway forecast</li>
                <li><Check />Cancel any time, no annual lock-in</li>
              </ul>
              <button type="button" className="btn btn-primary" onClick={ctaAction}>
                {isAuthenticated ? 'Open dashboard' : 'Get Solo'}
              </button>
            </div>

            <div className="price">
              <div>
                <div className="price-n">Team</div>
                <div className="price-amt">
                  <b>{money(teamSeatCents, symbol)}</b>
                  <span className="price-per">/ seat / month</span>
                </div>
                <p className="price-for">For when the second person joins.</p>
              </div>
              <ul>
                <li><Check />Everything in Solo, for every seat</li>
                <li><Check />Team Hub — workload and objectives</li>
                <li><Check />Group chat and live sessions</li>
                <li><Check /><span className="num">{teamSeatMin}</span> to <span className="num">{teamSeatLimit}</span> seats</li>
              </ul>
              <button type="button" className="btn btn-ghost" onClick={ctaAction}>
                {isAuthenticated ? 'Open dashboard' : 'Start a team'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── faq ────────────────────────────────────────────────────────── */}
      <section className="band band-sunk">
        <div className="shell">
          <div className="band-head band-head-c rise">
            <p className="eyebrow">Before you ask</p>
            <h2>The honest answers</h2>
          </div>
          <div className="faq rise">
            {FAQ.map(([q, a]) => (
              <div className="qa" key={q}>
                <h3>{q}</h3>
                <p>{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── closer ─────────────────────────────────────────────────────── */}
      <section className="band">
        <div className="shell">
          <div className="closer rise">
            <h2>Tomorrow morning, open one tab.</h2>
            <p>
              Start free, bring in whatever you are already tracking, and see if the
              8 a.m. version of you agrees.
            </p>
            <div className="hero-cta">
              <button type="button" className="btn btn-primary btn-lg" onClick={ctaAction}>
                {ctaLabel}
                <ArrowRight />
              </button>
              <a className="btn btn-ghost btn-lg" href="#pricing" onClick={jump('pricing')}>
                See pricing
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="foot">
        <div className="shell foot-in">
          <span>© {new Date().getFullYear()} Founders+ — built by a solo founder, for solo founders.</span>
          <nav className="foot-links">
            <a href="#pricing" onClick={jump('pricing')}>Pricing</a>
            <button type="button" onClick={() => onShowLegal('privacy')}>Privacy</button>
            <button type="button" onClick={() => onShowLegal('terms')}>Terms</button>
            <button type="button" onClick={() => onShowLegal('disclaimer')}>Disclaimer</button>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
